import "./App.css";
import "./SignCss.css";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { LanguageProvider } from "./context/LanguageProvider";
import { AuthProvider } from "./context/AuthContext";
import Login from "./pages/Login";
import ProtectedRoute from "./pages/ProtectedRoute";
import Tickets from "./components/Tickets";
import ManageUsers from "./components/ManageUsers";
import ProfileForm from "./pages/ProfilForm";
import Onboarding from "./pages/Onboarding";
import Profile from "./components/Profile";
import { Slide } from "@mui/material";
import Landing from "./pages/Landing";
import Pricing from "./pages/Pricing";
import PublicLayout from "./components/PublicLayout";
import AboutUs from "./components/AboutUs";
import Dashboard from "./components/Dashboard";
import TicketInsights from "./components/TicketInsights";
import Helpfaq from "./components/Helpfaq";
import GoToPremium from "./components/GoToPremium";
import PremiumSuccess from "./components/PremiumSuccess";
import PremiumCanceled from "./components/PremiumCanceled";
import SubscriptionCanceled from "./components/SubscriptionCanceled";
import SuperAdminSubscriptions from "./components/SuperAdminSubscriptions";
import { useMemo, useState, useEffect } from "react";
import { ThemeProvider, createTheme, CssBaseline } from "@mui/material";
import { logEvent } from "firebase/analytics";
import { analyticsPromise } from "./firebase";

const getInitialDarkMode = () => {
  try {
    const saved = localStorage.getItem("darkMode");
    if (saved !== null) return JSON.parse(saved); // true/false
  } catch {}
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches;
};

function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    let isMounted = true;
    analyticsPromise
      .then((analytics) => {
        if (analytics && isMounted) {
          logEvent(analytics, "page_view", {
            page_path: location.pathname + location.search,
          });
        }
      })
      .catch(() => {});
    return () => {
      isMounted = false;
    };
  }, [location]);
  return null;
}

function App() {
  const [darkMode, setDarkMode] = useState(getInitialDarkMode());
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: darkMode ? "dark" : "light",
          background: {
            default: darkMode ? "#121212" : "#f4f8f9",
            paper: darkMode ? "#1e1e1e" : "#ffffff",
          },
          ...(darkMode && { text: { primary: "#fff", secondary: "#ccc" } }),
        },
      }),
    [darkMode]
  );

  // Synchronise le fond <html>/<body> pour éviter tout flash entre pages
  useEffect(() => {
    const bg = theme.palette.background.default;
    document.documentElement.style.backgroundColor = bg;
    document.body.style.backgroundColor = bg;
  }, [theme]);

  // (Optionnel) mettre à jour localStorage et écouter un éventuel événement global
  useEffect(() => {
    try {
      localStorage.setItem("darkMode", JSON.stringify(darkMode));
    } catch {}
  }, [darkMode]);

  useEffect(() => {
    const onDarkChange = (e) => setDarkMode(Boolean(e.detail));
    window.addEventListener("darkmode:changed", onDarkChange);
    return () => window.removeEventListener("darkmode:changed", onDarkChange);
  }, []);

  return (
    // <BrowserRouter>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LanguageProvider>
        <ToastContainer
          position="top-center"
          autoClose={1000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="dark"
        />
        <PageViewTracker />
        <AuthProvider>
          <Routes>
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Landing />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/aboutus" element={<AboutUs />} />
            </Route>
            <Route path="/login" element={<Login />} />

            {/* Routes protégées — on « imbrie » les sous‑routes directement ici */}
            <Route path="/app" element={<ProtectedRoute />}>
              <Route index element={<Tickets />} />
              <Route path="projects/:projectId" element={<Tickets />} />
              <Route
                path="projects/:projectId/tickets/:ticketId"
                element={<Tickets />}
              />
              <Route path="userprofile" element={<Profile />} />
              <Route path="profile" element={<ProfileForm />} />
              <Route path="onboarding" element={<Onboarding />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="ticketinsights" element={<TicketInsights />} />
              <Route path="help" element={<Helpfaq />} />
              <Route path="manageusers" element={<ManageUsers />} />
              <Route path="gotopremium" element={<GoToPremium />} />
              <Route path="gotopremium/success" element={<PremiumSuccess />} />
              <Route path="gotopremium/cancel" element={<PremiumCanceled />} />
              <Route
                path="gotopremium/subscription-canceled"
                element={<SubscriptionCanceled />}
              />
              <Route
                path="admin/subscriptions"
                element={<SuperAdminSubscriptions />}
              />
            </Route>

            {/* fallback */}
            <Route path="*" element={<Landing />} />
          </Routes>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
